// What ImagePreviewDialog offers around the image. The gallery and the
// floorplan view take all of it; the project chat opens the same dialog on a
// message attachment, which is not gallery media (its file permission has no
// project and is never listed by the gallery), so it turns off everything that
// would write to, or read from, the gallery's own data. A switched-off
// capability hides its controls AND skips the loads it would otherwise fire
// on open. Viewing, zoom, paging, fullscreen and download are always there.
export const IMAGE_PREVIEW_CAPABILITIES_ALL = Object.freeze({
  edit: true, // description, rotate + save, delete (the pencil popover)
  tags: true, // media tag strip and the add-tag field (a chip row on a phone)
  interactions: true, // reaction buttons and the comments panel
  floorplans: true, // place-on-floorplan and the placement thumbnails
  activityLog: true, // VIEW_IMAGE logging and the "who viewed" list
  info: true, // added-by / project / size panel
});

/**
 * What the action bar offers: the viewer's capabilities, less the media tags
 * on a phone-sized screen (below md, where the viewer takes its phone
 * layout): the tag chips and the tag input do not fit in the top bar there,
 * and the row of chips at the bottom of the photo draws them instead (see
 * showsMobileTagRow). Only the bar's controls; what the tags capability
 * loads is the viewer's.
 * @param {Object} capabilities the viewer's capabilities
 * @param {boolean} isPhoneLayout the viewer is below md
 * @returns {Object} the same object when nothing is left out
 */
export const getActionBarCapabilities = (capabilities, isPhoneLayout) =>
  isPhoneLayout && capabilities.tags
    ? { ...capabilities, tags: false }
    : capabilities;

/**
 * Whether the viewer lays its bottom overlay over the photo: the comments
 * and, above their input, the tag row (showsMobileTagRow). On a phone-sized
 * screen and not in fullscreen; on a video only in the mobile viewer, since
 * a narrow desktop window keeps the video's native controls, which the
 * overlay would cover.
 * @param {Object} params
 * @param {boolean} params.isPhoneLayout the viewer is below md
 * @param {boolean} params.isFullScreen
 * @param {boolean} params.isVideo
 * @param {boolean} params.isMobileViewer the touch viewer (useMobilePhotoGestures)
 * @returns {boolean}
 */
export const showsBottomOverlay = ({
  isPhoneLayout,
  isFullScreen,
  isVideo,
  isMobileViewer,
}) => Boolean(isPhoneLayout && !isFullScreen && (!isVideo || isMobileViewer));

/**
 * Whether the viewer draws the media tags as the row of chips at the bottom
 * of the photo (MobileMediaTags/MobileMediaTagRow): wherever it has tags to
 * show and lays the bottom overlay over the photo (showsBottomOverlay), which
 * is below md, where the action bar leaves the tags out.
 * @param {Object} params
 * @param {Object} params.capabilities the viewer's capabilities
 * @param {boolean} params.isPhoneLayout the viewer is below md
 * @param {boolean} params.isFullScreen
 * @param {boolean} params.isVideo
 * @param {boolean} params.isMobileViewer the touch viewer (useMobilePhotoGestures)
 * @returns {boolean}
 */
export const showsMobileTagRow = ({ capabilities, ...layout }) =>
  Boolean(capabilities.tags) && showsBottomOverlay(layout);

export const IMAGE_PREVIEW_CAPABILITIES_VIEW_ONLY = Object.freeze({
  edit: false,
  tags: false,
  interactions: false,
  floorplans: false,
  activityLog: false,
  info: false,
});
